import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import App from './App';
import { applyDirOnLoad } from './lib/i18n';
import { applyBrandOnLoad } from './lib/brand';
import './styles/globals.css';

// Set <html dir> and <html lang> before React paints, so the first frame is
// already in the right direction — otherwise an Arabic customer sees a flash
// of LTR.
applyDirOnLoad();

// Same reason for the title, description and theme colour: index.html carries
// placeholders and this rewrites them from `brand.ts` before the first paint.
applyBrandOnLoad();

// ── No AuthProvider, no AddressProvider ────────────────────────────────────
// The source wrapped the tree in both, because it was a customer web app with
// a login and a saved-addresses book. This host has neither and must not: a
// payment page that can hold a session is a payment page that can be opened
// with someone else's, and every visitor here arrives from a tokenised link
// with no account. The token in the URL IS the credential, and the backend is
// what checks it. Both contexts were deleted, not left unused — an unused
// provider is an invitation to start using it.

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
